import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, FileText, Info } from "lucide-react";
import { submitNetlifyMultipart } from "@/lib/netlifyForm";
import { useHumanCheck, HumanCheckField } from "@/components/HumanCheck";
import { useSEO } from "@/lib/seo";

const field =
  "w-full bg-white border border-line rounded-[10px] px-4 py-3 text-base focus:outline-none focus:border-orange transition-colors duration-200";
const labelCls = "block text-sm font-bold uppercase tracking-wide text-ink mb-2";

const TYPES = ["Article rédigé", "Idée d'article", "Information", "Document"];

export default function Contribuer() {
  useSEO({
    title: "Contribuer",
    description:
      "Proposez un article, une idée de sujet ou transmettez une information ou un document à Contre-épreuve.",
  });
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialType = params.get("type") === "information" ? "Information" : "Article rédigé";

  const [form, setForm] = useState({
    name: "",
    email: "",
    proposal_type: initialType,
    subject: "",
    message: "",
    sources: "",
    consent: "",
    "bot-field": "",
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const check = useHumanCheck();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setChk = (k) => (e) => setForm({ ...form, [k]: e.target.checked ? "oui" : "" });

  const scrollToForm = () => {
    const el = document.getElementById("contribution-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    if (!check.isValid) {
      setError("Réponse incorrecte à la question de vérification.");
      check.regenerate();
      return;
    }
    if (form.consent !== "oui") {
      setError("Merci de confirmer votre consentement au traitement des données.");
      return;
    }
    setLoading(true);
    try {
      await submitNetlifyMultipart("contribution", form, file);
      navigate("/merci-contribution");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        navigate("/merci-contribution");
        return;
      }
      setError("L'envoi a échoué. Vérifiez votre connexion et réessayez.");
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-5 md:px-8 py-14 md:py-20">
      <header>
        <p className="text-orange font-bold text-sm uppercase tracking-wide mb-4">Contribuer</p>
        <h1 className="font-display font-black text-5xl md:text-6xl uppercase leading-[0.92] text-ink">
          Contribuer à Contre-épreuve
        </h1>
      </header>

      <div className="mt-8 prose-editorial text-lg">
        <p>
          Contre-épreuve peut recevoir des articles, des idées de sujets, des documents et des
          informations susceptibles de nourrir une publication.
        </p>
        <p>Il n'est pas nécessaire d'être journaliste professionnel pour proposer un contenu.</p>
      </div>

      <div className="mt-10 grid gap-5 sm:grid-cols-2">
        <div className="border border-line rounded-[16px] p-6 bg-white flex flex-col" style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-[12px] bg-highlight text-ink">
            <FileText size={20} strokeWidth={2.2} />
          </span>
          <h2 className="mt-4 font-display font-black text-2xl uppercase text-ink leading-none">Proposer un article</h2>
          <p className="mt-3 text-muted-ink flex-1">
            Tu peux transmettre un article déjà rédigé ou présenter un sujet que tu souhaiterais
            développer.
          </p>
        </div>
        <div className="border border-line rounded-[16px] p-6 bg-white flex flex-col" style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-[12px] bg-highlight text-ink">
            <Info size={20} strokeWidth={2.2} />
          </span>
          <h2 className="mt-4 font-display font-black text-2xl uppercase text-ink leading-none">Transmettre une information</h2>
          <p className="mt-3 text-muted-ink flex-1">
            Tu peux également signaler une situation ou transmettre un document sans proposer
            immédiatement un article.
          </p>
        </div>
      </div>

      <form
        id="contribution-form"
        name="contribution"
        method="POST"
        data-netlify="true"
        netlify-honeypot="bot-field"
        encType="multipart/form-data"
        onSubmit={submit}
        className="mt-12 space-y-6 bg-white border border-line rounded-[18px] p-6 md:p-8 scroll-mt-28"
        style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}
      >
        <input type="hidden" name="form-name" value="contribution" />
        <p className="hidden" aria-hidden="true">
          <label>
            Ne pas remplir : <input name="bot-field" tabIndex={-1} autoComplete="off" onChange={set("bot-field")} value={form["bot-field"]} />
          </label>
        </p>
        <div>
          <label htmlFor="contrib-name" className={labelCls}>Nom ou pseudonyme</label>
          <input id="contrib-name" name="name" required value={form.name} onChange={set("name")} data-testid="contrib-name" className={field} />
        </div>
        <div>
          <label htmlFor="contrib-email" className={labelCls}>Adresse électronique</label>
          <input id="contrib-email" name="email" type="email" required value={form.email} onChange={set("email")} data-testid="contrib-email" className={field} />
        </div>
        <div>
          <label htmlFor="contrib-type" className={labelCls}>Type de proposition</label>
          <select id="contrib-type" name="proposal_type" value={form.proposal_type} onChange={set("proposal_type")} data-testid="contrib-type" className={field}>
            {TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="contrib-subject" className={labelCls}>Titre ou sujet</label>
          <input id="contrib-subject" name="subject" value={form.subject} onChange={set("subject")} data-testid="contrib-subject" className={field} />
        </div>
        <div>
          <label htmlFor="contrib-message" className={labelCls}>Présentation de la proposition</label>
          <textarea id="contrib-message" name="message" required rows={7} value={form.message} onChange={set("message")} data-testid="contrib-message" className={field} />
        </div>
        <div>
          <label htmlFor="contrib-sources" className={labelCls}>Sources principales (facultatif)</label>
          <textarea id="contrib-sources" name="sources" rows={3} value={form.sources} onChange={set("sources")} data-testid="contrib-sources" className={field} />
        </div>
        <div>
          <label htmlFor="contrib-attachment" className={labelCls}>Pièce jointe (facultative)</label>
          <input
            id="contrib-attachment"
            name="attachment"
            type="file"
            onChange={(e) => setFile(e.target.files[0] || null)}
            data-testid="contrib-attachment"
            className="block w-full text-sm text-muted-ink file:mr-4 file:border file:border-line file:rounded-[8px] file:bg-paper file:px-4 file:py-2 file:font-bold file:text-xs file:uppercase file:text-ink hover:file:border-ink transition-colors"
          />
          <p className="mt-2 text-xs text-muted-ink">
            N'envoie pas de données sensibles qui ne sont pas nécessaires à l'examen de ta proposition.
          </p>
        </div>
        <HumanCheckField check={check} testid="contrib-human-check" />
        <label className="flex items-start gap-3 text-sm text-muted-ink cursor-pointer">
          <input type="checkbox" name="consent" checked={form.consent === "oui"} onChange={setChk("consent")} data-testid="contrib-consent" className="mt-1" />
          <span>J'accepte que les informations transmises soient traitées par la rédaction pour l'examen de ma proposition.</span>
        </label>
        {error && (
          <p role="alert" data-testid="contrib-error" className="text-sm font-medium text-[#B42318]">{error}</p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="contrib-submit"
          className="bg-orange text-ink font-bold rounded-[10px] px-7 py-3.5 uppercase tracking-wide hover:bg-ink hover:text-orange transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          {loading ? "Envoi en cours…" : "Envoyer la proposition"} <ArrowRight size={17} />
        </button>
        <p className="text-sm text-muted-ink">
          L'envoi d'une proposition ne garantit ni son utilisation ni sa publication. Aucun texte ne
          sera publié sous le nom d'un contributeur sans son accord.
        </p>
      </form>
    </div>
  );
}
